import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAccount } from '../../hooks/useAccount'

const buttonBase =
  'inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition'

function resolveAccountName(account) {
  return (
    account?.username ||
    account?.instagram_username ||
    account?.instagramUsername ||
    account?.display_name ||
    account?.displayName ||
    account?.handle ||
    null
  )
}

function resolveFollowerCount(account) {
  return (
    account?.followers_count ??
    account?.followersCount ??
    account?.followers ??
    null
  )
}

function AccountAvatar({ account }) {
  const accountName = resolveAccountName(account)

  if (!account?.profile_picture_url) {
    const initials = accountName?.slice(0, 2)?.toUpperCase() ?? 'IG'
    return (
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700">
        {initials}
      </div>
    )
  }

  return (
    <img
      src={account.profile_picture_url}
      alt={accountName ? `${accountName} profile` : 'Account avatar'}
      className="h-8 w-8 rounded-full object-cover"
    />
  )
}

function ChevronIcon({ open }) {
  return (
    <svg
      className={`h-4 w-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M5.23 7.21a.75.75 0 011.06.02L10 11.034l3.71-3.804a.75.75 0 111.08 1.04l-4.25 4.354a.75.75 0 01-1.08 0L5.25 8.27a.75.75 0 01-.02-1.06z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function LoadingSpinner() {
  return (
    <span className="inline-flex h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-transparent" />
  )
}

const keyCodes = {
  ENTER: 'Enter',
  SPACE: ' ',
  ESCAPE: 'Escape',
  ARROW_DOWN: 'ArrowDown',
  ARROW_UP: 'ArrowUp',
  TAB: 'Tab',
}

export default function AccountSelector({
  showManageAccounts = false,
  onManageAccounts,
  onAddAccount,
  emptyStateLabel = 'Add Instagram Account',
  className = '',
}) {
  const {
    accounts,
    selectedAccount,
    selectedAccountId,
    isLoadingAccounts,
    isChangingAccount,
    selectAccount,
    hasAccounts,
  } = useAccount()
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const triggerRef = useRef(null)
  const listRef = useRef(null)

  const menuId = 'account-selector-listbox'

  const options = useMemo(() => accounts ?? [], [accounts])
  const selectedAccountName = useMemo(
    () => resolveAccountName(selectedAccount),
    [selectedAccount]
  )
  const selectedFollowerCount = useMemo(
    () => resolveFollowerCount(selectedAccount),
    [selectedAccount]
  )

  useEffect(() => {
    if (!isOpen) {
      return
    }

    function handleClickOutside(event) {
      if (
        listRef.current &&
        !listRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false)
      }
    }

    function handleGlobalKeydown(event) {
      if (event.key === keyCodes.ESCAPE) {
        setIsOpen(false)
        triggerRef.current?.focus()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleGlobalKeydown)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleGlobalKeydown)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const index = options.findIndex((item) => item.id === selectedAccountId)
    setHighlightedIndex(index >= 0 ? index : 0)
  }, [isOpen, options, selectedAccountId])

  const handleToggle = useCallback(() => {
    if (isLoadingAccounts) {
      return
    }

    setIsOpen((prev) => !prev)
  }, [isLoadingAccounts])

  const handleKeyDown = useCallback(
    (event) => {
      if (!isOpen) {
        if (event.key === keyCodes.ARROW_DOWN || event.key === keyCodes.SPACE) {
          event.preventDefault()
          setIsOpen(true)
          return
        }
        return
      }

      if (event.key === keyCodes.ARROW_DOWN) {
        event.preventDefault()
        setHighlightedIndex((prev) => {
          const next = prev + 1
          return next >= options.length ? 0 : next
        })
      } else if (event.key === keyCodes.ARROW_UP) {
        event.preventDefault()
        setHighlightedIndex((prev) => {
          const next = prev - 1
          return next < 0 ? options.length - 1 : next
        })
      } else if (event.key === keyCodes.ENTER || event.key === keyCodes.SPACE) {
        event.preventDefault()
        const option = options[highlightedIndex]
        if (option) {
          selectAccount(option.id)
          setIsOpen(false)
          triggerRef.current?.focus()
        }
      } else if (event.key === keyCodes.TAB) {
        setIsOpen(false)
      }
    },
    [highlightedIndex, isOpen, options, selectAccount]
  )

  const handleAccountClick = useCallback(
    (accountId) => {
      selectAccount(accountId)
      setIsOpen(false)
      triggerRef.current?.focus()
    },
    [selectAccount]
  )

  const handleAddAccount = useCallback(() => {
    setIsOpen(false)
    if (onAddAccount) {
      onAddAccount()
    }
  }, [onAddAccount])

  const handleManageAccounts = useCallback(() => {
    setIsOpen(false)
    if (onManageAccounts) {
      onManageAccounts()
    }
  }, [onManageAccounts])

  if (!hasAccounts && !isLoadingAccounts) {
    if (!onAddAccount) {
      return (
        <div
          className={`inline-flex items-center rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-500 ${className}`.trim()}
        >
          No Instagram accounts connected
        </div>
      )
    }

    return (
      <button
        type="button"
        onClick={handleAddAccount}
        className={`${buttonBase} ${className}`.trim()}
      >
        {emptyStateLabel}
      </button>
    )
  }

  return (
    <div className={`relative ${className}`.trim()}>
      <button
        ref={triggerRef}
        type="button"
        className={`${buttonBase} min-w-[180px] justify-between gap-2`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={menuId}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        disabled={isLoadingAccounts}
      >
        <span className="flex items-center gap-2">
          {(isLoadingAccounts || isChangingAccount) ? (
            <LoadingSpinner />
          ) : (
            <AccountAvatar account={selectedAccount} />
          )}
          <span className="flex flex-col items-start">
            <span className="text-sm font-medium text-gray-900">
              {selectedAccountName ?? 'Select account'}
            </span>
            <span className="text-xs text-gray-500">
              {selectedFollowerCount
                ? `${selectedFollowerCount.toLocaleString()} followers`
                : 'Instagram account'}
            </span>
          </span>
        </span>
        <ChevronIcon open={isOpen} />
      </button>

      {isOpen && (
        <div
          ref={listRef}
          id={menuId}
          role="listbox"
          aria-activedescendant={
            options[highlightedIndex]
              ? `account-option-${options[highlightedIndex].id}`
              : undefined
          }
          tabIndex={-1}
          className="absolute right-0 z-30 mt-2 w-64 origin-top-right rounded-md border border-gray-200 bg-white shadow-lg focus:outline-none"
        >
          <div className="max-h-80 overflow-y-auto py-2">
            {isLoadingAccounts && (
              <div className="flex items-center justify-center px-4 py-6 text-sm text-gray-500">
                Loading accounts...
              </div>
            )}

            {!isLoadingAccounts && options.length === 0 && (
              <div className="px-4 py-6 text-sm text-gray-500">
                No Instagram accounts connected yet.
              </div>
            )}

            {options.map((account, index) => {
              const isSelected = account.id === selectedAccountId
              const isHighlighted = index === highlightedIndex

              return (
                <button
                  key={account.id}
                  type="button"
                  role="option"
                  id={`account-option-${account.id}`}
                  aria-selected={isSelected}
                  className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition ${
                    isHighlighted ? 'bg-gray-100' : ''
                  } ${isSelected ? 'font-medium text-primary-600' : 'text-gray-700'}`}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => handleAccountClick(account.id)}
                >
                  <AccountAvatar account={account} />
                  <div className="flex flex-col">
                    <span>{resolveAccountName(account) ?? 'Instagram account'}</span>
                    {resolveFollowerCount(account) ? (
                      <span className="text-xs text-gray-500">
                        {resolveFollowerCount(account).toLocaleString()} followers
                      </span>
                    ) : null}
                  </div>
                  {isSelected && (
                    <svg
                      className="ml-auto h-4 w-4 text-primary-600"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.704 5.29a1 1 0 00-1.408-1.42L8 10.586 4.704 7.29A1 1 0 003.296 8.71l4 4a1 1 0 001.408 0l8-8z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
              )
            })}
          </div>

          {(showManageAccounts || onAddAccount) && (
            <div className="border-t border-gray-100 px-2 py-2">
              {onAddAccount && (
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-primary-600 hover:bg-primary-50"
                  onClick={handleAddAccount}
                >
                  <span className="h-5 w-5 rounded-full border border-primary-300 text-center text-base leading-5 text-primary-600">
                    +
                  </span>
                  Add account
                </button>
              )}
              {showManageAccounts && onManageAccounts && (
                <button
                  type="button"
                  className="flex w-full items-center rounded-md px-3 py-2 text-sm text-gray-600 hover:bg-gray-100"
                  onClick={handleManageAccounts}
                >
                  Manage accounts
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
